import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { formatErr } from "../lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function dashboardFor(user) {
  if (user?.role === "admin") return "/admin";
  if (user?.role === "dealer") return "/dealer";
  return "/client";
}

function validateIdentifier(value) {
  const trimmed = value.trim();
  if (!trimmed) return "Mobile number or email is required.";
  if (trimmed.includes("@")) {
    return EMAIL_RE.test(trimmed.toLowerCase()) ? "" : "Enter a valid email address.";
  }
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 15) return "Enter a valid mobile number.";
  return "";
}

export default function LoginModal({ open, onClose }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const identifierRef = useRef(null);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => identifierRef.current?.focus(), 80);
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      setServerError("");
      setErrors({});
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (event) => {
    event.preventDefault();
    const nextErrors = {
      identifier: validateIdentifier(identifier),
      password: password ? "" : "Password is required.",
    };
    setErrors(nextErrors);
    setServerError("");
    if (nextErrors.identifier || nextErrors.password) return;

    try {
      setBusy(true);
      const user = await login(identifier.trim(), password, rememberMe);
      onClose?.();
      navigate(dashboardFor(user), { replace: true });
    } catch (err) {
      const message = formatErr(err);
      setServerError(message || "Unable to sign in. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="panel-login-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose?.();
      }}
    >
      <div className="w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-200 overflow-hidden rounded-3xl border border-white/40 bg-white/90 shadow-2xl shadow-slate-900/20 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/90">
        <div className="flex items-center justify-between border-b border-slate-200/80 px-6 py-5 dark:border-white/10">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-600 dark:text-blue-300">Secure sign in</div>
            <h2 id="panel-login-title" className="mt-1 text-2xl font-black tracking-tight text-slate-950 dark:text-white">Welcome Back</h2>
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose?.()}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            aria-label="Close login popup"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5 px-6 py-6">
          {serverError && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
              {serverError}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="login-identifier" className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Mobile Number or Email</Label>
            <Input
              ref={identifierRef}
              id="login-identifier"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              placeholder="Mobile Number or Email"
              autoComplete="username"
              inputMode="email"
              className="h-12 rounded-2xl border-slate-200 bg-white/80 text-base shadow-sm focus-visible:ring-blue-600 dark:border-white/10 dark:bg-white/5 dark:text-white"
              aria-invalid={Boolean(errors.identifier)}
              aria-describedby={errors.identifier ? "login-identifier-error" : undefined}
            />
            {errors.identifier && <p id="login-identifier-error" className="text-sm font-medium text-red-600 dark:text-red-300">{errors.identifier}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password" className="text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Password</Label>
            <Input
              id="login-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="h-12 rounded-2xl border-slate-200 bg-white/80 text-base shadow-sm focus-visible:ring-blue-600 dark:border-white/10 dark:bg-white/5 dark:text-white"
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? "login-password-error" : undefined}
            />
            {errors.password && <p id="login-password-error" className="text-sm font-medium text-red-600 dark:text-red-300">{errors.password}</p>}
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <Checkbox checked={rememberMe} onCheckedChange={(checked) => setRememberMe(Boolean(checked))} />
              Remember Me
            </label>
            <button
              type="button"
              onClick={() => setServerError("Please contact your dealer or administrator to reset your password.")}
              className="text-sm font-black text-blue-700 hover:text-blue-900 dark:text-blue-300"
            >
              Forgot Password?
            </button>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="h-12 w-full rounded-2xl bg-slate-950 text-base font-black text-white shadow-lg shadow-slate-950/20 transition hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
            {busy ? "Logging in..." : "Login"}
          </Button>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            <span>Don&apos;t have an account?</span>
            <span className="ml-1 font-black text-slate-950 dark:text-white">Contact your Dealer</span>
          </div>
        </form>
      </div>
    </div>
  );
}
